import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScrollText, ArrowLeft, Plus, X, AlertCircle, Trash2, Save } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type { Audit, AuditStatus, FindingType } from '../../../types/whs';
import {
  Badge, AUDIT_TYPE_LABEL, AUDIT_STATUS_LABEL, AUDIT_STATUS_CLASS,
  FINDING_TYPE_LABEL, FINDING_TYPE_CLASS,
} from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const STATUS_OPTIONS: AuditStatus[] = ['planned', 'in_progress', 'completed'];
const FINDING_TYPES: FindingType[] = [
  'nonconformity_major', 'nonconformity_minor', 'observation', 'opportunity', 'conformity',
];

function userName(u: UserData): string {
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return full || u.username;
}

function WhsAuditDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const auditId = Number(id);

  const [audit, setAudit] = useState<Audit | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [clauseRef, setClauseRef] = useState('');
  const [finding, setFinding] = useState('');
  const [findingType, setFindingType] = useState<FindingType>('observation');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.getAudit(auditId);
    if (res.success) {
      setAudit(res.data);
      setSummary(res.data.summary || '');
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  }, [auditId]);

  const loadUsers = useCallback(async () => {
    const res = await userService.getUsers();
    if (res.success) setUsers(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const auditorLabel = (): string => {
    if (!audit) return '—';
    if (audit.auditor_name) return audit.auditor_name;
    if (audit.auditor_id != null) {
      const u = users.find((x) => x.id === audit.auditor_id);
      if (u) return userName(u);
    }
    return '—';
  };

  const changeStatus = async (status: AuditStatus) => {
    setSaving(true);
    setError(null);
    const res = await whsService.updateAudit(auditId, { status });
    setSaving(false);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const saveSummary = async () => {
    setSaving(true);
    setError(null);
    const res = await whsService.updateAudit(auditId, { summary });
    setSaving(false);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const openModal = () => {
    setFormError(null);
    setClauseRef('');
    setFinding('');
    setFindingType('observation');
    setShowModal(true);
  };

  const handleAddFinding = async () => {
    if (!finding.trim()) { setFormError('Finding is required.'); return; }
    setSaving(true);
    setFormError(null);
    const res = await whsService.createAuditFinding(auditId, {
      clause_ref: clauseRef.trim() || null,
      finding: finding.trim(),
      finding_type: findingType,
    });
    setSaving(false);
    if (res.success) {
      setShowModal(false);
      await load();
    } else {
      setFormError(res.error.message);
    }
  };

  const toggleFinding = async (fid: number, current: 'open' | 'closed') => {
    setSaving(true);
    setError(null);
    const res = await whsService.updateAuditFinding(fid, { status: current === 'open' ? 'closed' : 'open' });
    setSaving(false);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const removeFinding = async (fid: number) => {
    setSaving(true);
    setError(null);
    const res = await whsService.deleteAuditFinding(fid);
    setSaving(false);
    if (res.success) await load();
    else setError(res.error.message);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={audit ? audit.title : 'Audit'}
        subtitle="Audit record, findings & non-conformities"
        icon={<ScrollText className="h-6 w-6 text-white" />}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/hr/whs/audits')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back to audits
        </button>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : !audit ? (
          <p className="text-sm text-neutral-500">Audit not found.</p>
        ) : (
          <>
            {/* Header card */}
            <div className="rounded-xl border border-neutral-200 bg-white p-5 mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-neutral-500">Type</p>
                    <p className="font-medium text-neutral-900">{AUDIT_TYPE_LABEL[audit.audit_type]}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Standard</p>
                    <p className="font-medium text-neutral-900">{audit.standard_ref || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Auditor</p>
                    <p className="font-medium text-neutral-900">{auditorLabel()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Audit date</p>
                    <p className="font-medium text-neutral-900">
                      {audit.audit_date ? formatLocalDateInTz(audit.audit_date, orgTz) : '—'}
                    </p>
                  </div>
                  <div className="col-span-2 sm:col-span-3">
                    <p className="text-xs text-neutral-500">Scope</p>
                    <p className="font-medium text-neutral-900 whitespace-pre-wrap">{audit.scope || '—'}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge label={AUDIT_STATUS_LABEL[audit.status]} className={AUDIT_STATUS_CLASS[audit.status]} />
                  <select
                    value={audit.status}
                    onChange={(e) => changeStatus(e.target.value as AuditStatus)}
                    disabled={saving}
                    className="rounded-lg border border-neutral-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{AUDIT_STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-xl border border-neutral-200 bg-white p-5 mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Summary</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Overall audit summary…"
              />
              <div className="mt-3 flex justify-end">
                <Button variant="secondary" size="sm" onClick={saveSummary} isLoading={saving}>
                  <Save className="h-4 w-4" /> Save summary
                </Button>
              </div>
            </div>

            {/* Findings */}
            <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
                <h3 className="text-sm font-semibold text-neutral-900">Findings ({audit.findings.length})</h3>
                <Button size="sm" onClick={openModal}>
                  <Plus className="h-4 w-4" /> Add finding
                </Button>
              </div>
              {audit.findings.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-neutral-500">No findings recorded yet.</p>
              ) : (
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Clause</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Finding</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {audit.findings.map((f) => (
                      <tr key={f.id}>
                        <td className="px-4 py-3 text-sm text-neutral-600 align-top">{f.clause_ref || '—'}</td>
                        <td className="px-4 py-3 text-sm text-neutral-900 align-top whitespace-pre-wrap">{f.finding}</td>
                        <td className="px-4 py-3 align-top">
                          <Badge label={FINDING_TYPE_LABEL[f.finding_type]} className={FINDING_TYPE_CLASS[f.finding_type]} />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <Badge
                            label={f.status === 'open' ? 'Open' : 'Closed'}
                            className={f.status === 'open' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}
                          />
                        </td>
                        <td className="px-4 py-3 text-right align-top">
                          <div className="inline-flex gap-2">
                            <Button variant="secondary" size="sm" onClick={() => toggleFinding(f.id, f.status)} disabled={saving}>
                              {f.status === 'open' ? 'Close' : 'Reopen'}
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => removeFinding(f.id)} disabled={saving} aria-label="Delete finding">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-neutral-900">Add finding</h2>
              <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              {formError && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Clause reference</label>
                <input
                  value={clauseRef}
                  onChange={(e) => setClauseRef(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g. 8.1.2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Finding <span className="text-red-500">*</span></label>
                <textarea
                  value={finding}
                  onChange={(e) => setFinding(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Describe the finding…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Finding type</label>
                <select
                  value={findingType}
                  onChange={(e) => setFindingType(e.target.value as FindingType)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {FINDING_TYPES.map((t) => (
                    <option key={t} value={t}>{FINDING_TYPE_LABEL[t]}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAddFinding} isLoading={saving}>Add</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhsAuditDetail() {
  return <WhsGate><WhsAuditDetailInner /></WhsGate>;
}
